import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useOpen } from 'hooks/use-open';
import { useClickOutside } from 'hooks/use-click-outside';

import { useAidResults, useAidResultsCoa } from '../../hooks/use-aid-results';
import { useAidModel } from '../../hooks/use-aid-model-details';
import { useCurrentUser } from 'hooks/api/use-current-user';

import {
  formatCount,
  formatCurrency,
  formatRate,
} from '../../steps/results-step/helpers/results-format';
import { buildCoaGroupSummaries } from '../../steps/results-step/helpers/build-coa-rows';

import type { AidModelDto } from 'api/generated/schemas';

import type { RefObject } from 'react';

export interface QuickSummaryTuitionGroup {
  name: string;
  value: string;
}

export interface QuickSummaryData {
  tuitionFutureYearLabel: string;
  tuitionCurrentYearLabel: string;
  estimatedEnrollment: string;
  estimatedNtr: string;
  estimatedAvgNtr: string;
  estimatedDiscountRate: string;
  enrollmentChange: string | null;
  hasActualData: boolean;
  actualEnrollment: string;
  actualNtr: string;
  actualAvgNtr: string;
  actualDiscountRate: string;
  hasTuitionData: boolean;
  tuitionFutureYearGroups: QuickSummaryTuitionGroup[];
  tuitionCurrentYearGroups: QuickSummaryTuitionGroup[];
}

interface UseQuickSummaryParams {
  clientId: string;
  isNewModel: boolean;
}

interface UseQuickSummaryResult {
  isOpen: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  data: QuickSummaryData | null;
  panelRef: RefObject<HTMLDivElement | null>;
  open: () => void;
  close: () => void;
}

const computeAvgNtr = (
  ntr: number | undefined,
  enrollment: number | undefined
): string => {
  if (ntr == null || enrollment == null || enrollment === 0) return '—';

  return formatCurrency(ntr / enrollment);
};

const formatEnrollmentChange = (
  estimated: number | undefined,
  actual: number | undefined
): string | null => {
  if (estimated == null || actual == null) return null;
  const diff = Math.round(estimated - actual);

  if (diff === 0) return null;

  const sign = diff > 0 ? '+' : '';
  const pct =
    actual !== 0 ? ` (${((Math.abs(diff) / actual) * 100).toFixed(0)}%)` : '';

  return `${sign}${diff.toLocaleString('en-US')}${pct}`;
};

export const useQuickSummary = ({
  clientId,
  isNewModel,
}: UseQuickSummaryParams): UseQuickSummaryResult => {
  const { modelId } = useParams();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';

  const { isOpen, handleOpen, handleClose } = useOpen();
  const panelRef = useRef<HTMLDivElement>(null);

  useClickOutside(panelRef, handleClose, isOpen);

  const resultsQuery = useAidResults({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId && isOpen,
  });

  const coaQuery = useAidResultsCoa({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId && isOpen,
  });

  const modelQuery = useAidModel({
    userId,
    clientId,
    modelId: modelId ?? '',
    enabled: !!modelId && !!clientId && isOpen,
  });

  const open = useCallback(() => {
    if (!modelId || isNewModel) return;
    handleOpen();
  }, [modelId, isNewModel, handleOpen]);

  const entryTerm = (modelQuery.data as AidModelDto | undefined)?.entryTerm;

  const data = useMemo((): QuickSummaryData | null => {
    if (!resultsQuery.data) return null;

    const estimated = resultsQuery.data.estimatedResults?.totalFirstYear;
    const actual = resultsQuery.data.actualResults?.totalFirstYear;

    const { tuitionFees } = buildCoaGroupSummaries(coaQuery.data ?? []);

    return {
      tuitionFutureYearLabel: entryTerm ? String(entryTerm + 1) : 'Future Year',
      tuitionCurrentYearLabel: entryTerm ? String(entryTerm) : 'Current Year',
      estimatedEnrollment: formatCount(estimated?.enrollment),
      estimatedNtr: formatCurrency(estimated?.netTuitionRevenue),
      estimatedAvgNtr: computeAvgNtr(
        estimated?.netTuitionRevenue,
        estimated?.enrollment
      ),
      estimatedDiscountRate: formatRate(estimated?.tuitionDiscountRate),
      enrollmentChange: formatEnrollmentChange(
        estimated?.enrollment,
        actual?.enrollment
      ),
      hasActualData:
        actual?.enrollment != null ||
        actual?.netTuitionRevenue != null ||
        actual?.tuitionDiscountRate != null,
      actualEnrollment: formatCount(actual?.enrollment),
      actualNtr: formatCurrency(actual?.netTuitionRevenue),
      actualAvgNtr: computeAvgNtr(
        actual?.netTuitionRevenue,
        actual?.enrollment
      ),
      actualDiscountRate: formatRate(actual?.tuitionDiscountRate),
      hasTuitionData: !!tuitionFees.length,
      // Backend COA field names are swapped vs their meaning: previousYearAmount is the
      // actual current-year value, currentYearAmount is the projected future-year value.
      tuitionCurrentYearGroups: tuitionFees
        .filter((group) => !!group.previousYearAmount)
        .map((group) => ({
          name: group.name,
          value: formatCurrency(group.previousYearAmount),
        })),
      tuitionFutureYearGroups: tuitionFees
        .filter((group) => !!group.currentYearAmount)
        .map((group) => ({
          name: group.name,
          value: formatCurrency(group.currentYearAmount),
        })),
    };
  }, [resultsQuery.data, coaQuery.data, entryTerm]);

  const isLoading =
    isOpen && (resultsQuery.isLoading || resultsQuery.isFetching);

  const isError = resultsQuery.isError;
  const errorMessage =
    (resultsQuery.error as { data?: { detail?: string } })?.data?.detail ||
    'Failed to load summary data';

  return {
    isOpen,
    isLoading,
    isError,
    errorMessage,
    data,
    panelRef,
    open,
    close: handleClose,
  };
};
